import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  IconPicker,
  Input,
  Switch,
  Text,
  type Color,
  type IconName,
} from "@salestrak/ui";

import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CreateTeamDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (teamId: string) => void;
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toKey(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 6)
    .toUpperCase();
}

// ---------------------------------------------------------------------------
// Component — the /settings/teams/create form as a dialog, mirroring
// CreateWorkflowDialog. One Dialog at every breakpoint. The icon picker
// behaves like the team edit page's Name & Icon row.
// ---------------------------------------------------------------------------

export function CreateTeamDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateTeamDialogProps) {
  const { createTeam, validateTeamKey } = useTeamMutations();

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [autoKey, setAutoKey] = useState(true);
  const [iconName, setIconName] = useState<IconName>(ENTITY_KIND_ICON.team);
  const [iconColor, setIconColor] = useState<Color>("dark-grey");
  const [isPrivate, setIsPrivate] = useState(false);

  const [nameError, setNameError] = useState("");
  const [keyError, setKeyError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Key validation debounce
  const keyValidateRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setName("");
      setKey("");
      setAutoKey(true);
      setIconName(ENTITY_KIND_ICON.team);
      setIconColor("dark-grey");
      setIsPrivate(false);
      setNameError("");
      setKeyError("");
      setSubmitting(false);
    }
  }, [open]);

  // Auto-populate key from name until the user edits the key directly.
  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      setNameError("");
      if (autoKey) {
        setKey(toKey(value));
        setKeyError("");
      }
    },
    [autoKey],
  );

  const handleKeyChange = useCallback((value: string) => {
    setAutoKey(false);
    const upper = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase();
    setKey(upper);
    setKeyError("");
  }, []);

  // Debounced key uniqueness validation
  useEffect(() => {
    if (!key || key.length < 3) {
      setKeyError("");
      return;
    }

    clearTimeout(keyValidateRef.current);
    keyValidateRef.current = setTimeout(async () => {
      try {
        const available = await validateTeamKey(key);
        if (!available) {
          setKeyError("Key already in use");
        } else {
          setKeyError("");
        }
      } catch {
        // Ignore validation errors
      }
    }, 250);

    return () => clearTimeout(keyValidateRef.current);
  }, [key, validateTeamKey]);

  const canSubmit = name.trim().length >= 3 && !keyError && !submitting;

  const handleSubmit = useCallback(async () => {
    if (name.trim().length < 3) {
      setNameError("Name must be at least 3 characters");
      return;
    }

    setSubmitting(true);
    try {
      // createTeam toasts success/failure itself and returns null on error.
      const result = await createTeam({
        teamName: name.trim(),
        teamKey: key.trim() || undefined,
        icon: { icon: iconName, color: iconColor },
        private: isPrivate,
      });

      if (result && "teamId" in result) {
        onOpenChange(false);
        onCreated?.(result.teamId);
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    name,
    key,
    iconName,
    iconColor,
    isPrivate,
    createTeam,
    onOpenChange,
    onCreated,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>Create Team</DialogTitle>
        </DialogHeader>
        <Box
          as="form"
          gap="lg"
          paddingX="lg"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) void handleSubmit();
          }}
        >
          <Field invalid={!!nameError}>
            <FieldLabel>Team name</FieldLabel>
            <Box direction="row" gap="sm" align="center">
              <IconPicker
                icon={iconName}
                color={iconColor}
                onIconChange={setIconName}
                onColorChange={setIconColor}
                size="sm"
              />
              <Box fill="flex" shrink>
                <Input
                  placeholder="e.g. Boise Sales"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  autoFocus
                />
              </Box>
            </Box>
            {nameError ? <FieldError match>{nameError}</FieldError> : null}
          </Field>

          <Field invalid={!!keyError}>
            <FieldLabel>Identifier</FieldLabel>
            <Input
              placeholder="e.g. BOISE"
              value={key}
              onChange={(e) => handleKeyChange(e.target.value)}
              maxLength={6}
            />
            {keyError ? (
              <FieldError match>{keyError}</FieldError>
            ) : (
              <FieldDescription>
                A short key for this team. Auto-generated from name.
              </FieldDescription>
            )}
          </Field>

          <Box direction="row" align="center" justify="between" gap="sm">
            <Box gap="2xs" fill="flex" shrink>
              <Text size="sm" weight="medium">
                Private team
              </Text>
              <Text size="xs" variant="muted">
                Only team members can see private teams and their issues.
              </Text>
            </Box>
            <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
          </Box>
        </Box>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
